import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

interface TopGame {
    id: number;
    game_id: number;
    game_name: string;
    game_slug: string;
    game_image: string | null;
    user_id: number;
    user_name: string;
    description: string;
    created_at: string;
}

interface TopGamesCarouselProps {
    autoRotate?: boolean;
    rotationInterval?: number;
}

const TopGamesCarousel: React.FC<TopGamesCarouselProps> = ({
    autoRotate = true,
    rotationInterval = 5000
}) => {
    const [topGames, setTopGames] = useState<TopGame[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [fade, setFade] = useState(true);
    const [itemsPerSlide, setItemsPerSlide] = useState(2);
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        // Megakadályozza az Effect újbóli futását, még ha a komponens remount-olódik
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;

        const fetchTopGames = async () => {
            try {
                console.log("Fetching TOP games from /api/top_games/latest...");
                const response = await fetch("/api/top_games/latest");
                console.log("Response status:", response.status);

                if (!response.ok) throw new Error("TOP játékok betöltése sikertelen");

                const data = await response.json();
                console.log("TOP Games data:", data);

                setTopGames(data.data || []);
                setError(null);
            } catch (error) {
                console.error("Error fetching top games:", error);
                setError(error instanceof Error ? error.message : "Ismeretlen hiba");
            } finally {
                setLoading(false);
            }
        };

        fetchTopGames();
    }, []);

    // Handle responsive layout
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) { // md breakpoint
                setItemsPerSlide(2);
            } else {
                setItemsPerSlide(1); // Mobile: 1 item per slide
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!autoRotate || topGames.length <= itemsPerSlide) return;

        const interval = setInterval(() => {
            setFade(false);
            setTimeout(() => {
                setCurrentIndex((prev) => (prev + itemsPerSlide) % topGames.length);
                setFade(true);
            }, 300);
        }, rotationInterval);

        return () => clearInterval(interval);
    }, [autoRotate, rotationInterval, topGames.length, itemsPerSlide]);

    if (loading) {
        return (
            <div className="my-8 px-4">
                <div className="bg-gray-800 rounded-lg p-8 text-center border border-gray-700">
                    <p className="text-muted-foreground">TOP ajánlások betöltése...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="my-8 px-4">
                <div className="bg-gray-800 rounded-lg p-8 text-center border border-red-700">
                    <p className="text-red-400">Hiba: {error}</p>
                </div>
            </div>
        );
    }

    if (topGames.length === 0) {
        return (
            <div className="my-8 px-4">
                <div className="bg-gray-800 rounded-lg p-8 text-center border border-gray-700">
                    <p className="text-muted-foreground">Nincs TOP ajánlás még.</p>
                </div>
            </div>
        );
    }

    // Get current items to display
    const displayedGames = topGames.slice(currentIndex, currentIndex + itemsPerSlide);
    const hasMultipleSlides = topGames.length > itemsPerSlide;

    return (
        <div className="my-8 px-4">
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-opacity duration-300 ${fade ? 'opacity-100' : 'opacity-0'}`}>
                {displayedGames.map((game) => (
                    <Link
                        key={game.id}
                        to={`/games/${game.game_slug}`}
                        className="group relative bg-gray-800 rounded-lg overflow-hidden border border-gray-700 hover:border-primary transition-all duration-300 transform hover:scale-105 block h-full"
                    >
                        {/* Game Image */}
                        <div className="relative h-56 overflow-hidden bg-gray-900">
                            <img
                                src={game.game_image || 'https://via.placeholder.com/300x200?text=No+Image'}
                                alt={game.game_name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200?text=No+Image';
                                }}
                            />
                            {/* Overlay gradient */}
                            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
                        </div>

                        {/* Game Name */}
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-gray-900 to-transparent">
                            <h3 className="text-lg font-bold text-white line-clamp-2">
                                {game.game_name}
                            </h3>
                        </div>

                        {/* Badge */}
                        <div className="absolute top-3 right-3 bg-yellow-500 text-gray-900 px-3 py-1 rounded-full text-xs font-bold">
                            TOP
                        </div>
                    </Link>
                ))}
            </div>

            {/* Carousel Indicators */}
            {hasMultipleSlides && (
                <div className="flex justify-center gap-2 mt-6">
                    {Array.from({ length: Math.ceil(topGames.length / itemsPerSlide) }).map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => {
                                setFade(false);
                                setTimeout(() => {
                                    setCurrentIndex(idx * itemsPerSlide);
                                    setFade(true);
                                }, 300);
                            }}
                            className={`h-2 rounded-full transition-all duration-300 ${idx * itemsPerSlide === currentIndex
                                ? 'bg-primary w-8'
                                : 'bg-gray-600 w-2 hover:bg-gray-500'
                                }`}
                            aria-label={`Go to slide ${idx + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default TopGamesCarousel;
